/**
 * O que o núcleo precisa de cada plataforma.
 *
 * Os módulos daqui para baixo (sessão, ajustes, armazém em React) são iguais no
 * site, no Electron e no celular. O que muda são três coisas: onde a API mora,
 * como o cookie viaja e onde se guarda um par de chaves entre aberturas. Cada
 * app monta essas três e chama `ligar()` uma vez, antes do primeiro render.
 *
 * É um objeto de módulo e não um contexto de React de propósito: o armazém e o
 * cliente já são únicos por app, e passá-los por contexto obrigaria todo hook a
 * carregar um `useContext` para chegar a algo que nunca muda.
 */

import type { Armazem } from '../armazem';
import type { NossoSempreAPI } from '../api/client';

/**
 * O canto das miudezas que o cookie não conta e que precisam sobreviver ao app
 * fechado: quem entrou, se este aparelho foi trancado à mão, a língua escolhida,
 * a estante de livros, o histórico do AnimeX. Nada de credencial — o `ns_sess` é
 * `HttpOnly` e nem o JavaScript nem isto aqui têm como lê-lo.
 *
 * Síncrono porque o `localStorage` é, e porque a sessão precisa do valor no
 * primeiro render. No celular não existe leitura síncrona de disco: quem chama
 * lê a lista inteira antes de montar a árvore e entrega um espelho em memória
 * (veja `CHAVES` em `packages/mobile/src/nucleo/api.ts`). Por isso chave nova
 * aqui é sempre chave nova lá também — e é o `mobile/test/guardado.mjs` que
 * cobra, porque esquecer não dá erro nenhum, só apaga o que estava guardado.
 */
export interface Guardado {
  ler(chave: string): string | null;
  gravar(chave: string, valor: string): void;
  apagar(chave: string): void;
}

export interface Ligacao {
  api: NossoSempreAPI;
  armazem: Armazem;
  guardado: Guardado;
  /**
   * Se esta plataforma consegue ouvir o fluxo. Onde não dá (React Native não
   * tem `EventSource`), o núcleo recarrega as coleções à mão.
   */
  aoVivo: boolean;
}

let atual: Ligacao | null = null;

export function ligar(l: Ligacao): void {
  atual = l;
}

export function ligacao(): Ligacao {
  if (!atual) throw new Error('Chame ligar() antes de usar o núcleo.');
  return atual;
}

/**
 * Quem cuidar da sessão registra aqui o que fazer quando o servidor disser 401.
 * Só existe um interessado — o provedor de sessão — e é ele quem devolve a
 * pessoa para a porta.
 */
let expirou: (() => void) | null = null;

export function quandoExpirar(f: () => void): () => void {
  expirou = f;
  return () => {
    if (expirou === f) expirou = null;
  };
}

/** O que o `APIConfig.aoExpirar` de cada app deve chamar. */
export function expirar(): void {
  expirou?.();
}

/** Um `Guardado` em memória, para plataforma sem armazenamento síncrono. */
export function guardadoEmMemoria(inicial: Record<string, string> = {}): Guardado & {
  aoMudar?: (chave: string, valor: string | null) => void;
} {
  const mapa = new Map(Object.entries(inicial));
  const g = {
    ler: (c: string) => mapa.get(c) ?? null,
    gravar(c: string, v: string) {
      mapa.set(c, v);
      g.aoMudar?.(c, v);
    },
    apagar(c: string) {
      mapa.delete(c);
      g.aoMudar?.(c, null);
    },
  } as Guardado & { aoMudar?: (chave: string, valor: string | null) => void };
  return g;
}
